import {css} from '@emotion/react'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'

export const FieldWrapperStyles = css`
  gap: ${cssTheme.layout.spacing.s200};
  display: flex;
  flex-direction: column;
  width: 100%;
  /* This padding prevents the outline from being cut off when the input is focused */
  padding: 1px;
`

export const FieldStyles = css`
  align-items: center;
  background: ${cssTheme.colors.input.default.background};
  border-radius: ${cssTheme.layout.radius.md};
  border: 1px solid ${cssTheme.colors.input.default.border};
  display: flex;
  gap: ${cssTheme.layout.spacing.s250};
  height: 48px;
  padding: 0 ${cssTheme.layout.spacing.s400};

  &:focus-within,
  &.--is-filled {
    .label {
      ${cssTheme.typography.bodyXS};

      background: ${cssTheme.colors.background.neutral.primary};
      padding: 0 ${cssTheme.layout.spacing.s200};
      transform: translateY(-19px) translateX(-4px);
      height: auto;
    }

    /* Hack to vertically align the label with the Field on mobile Safari */
    @supports (-webkit-touch-callout: none) {
      .label {
        transform: translateY(-19px) translateX(-4px);
      }
    }
  }

  &:focus-within {
    outline: 1px solid ${cssTheme.colors.border.interactive.primaryActive};
    border-color: ${cssTheme.colors.border.interactive.primaryActive};
  }

  &:focus-within,
  &:not(.--is-disabled):not(.--is-invalid).--is-filled {
    .label {
      color: ${cssTheme.colors.input.default.text};
    }
  }

  &.--is-invalid {
    &:not(:focus-within) {
      border-color: ${cssTheme.colors.input.error.border};
      &.--is-filled {
        .label {
          color: ${cssTheme.colors.input.error.caption};
        }
      }
    }
  }

  &:not(.--is-disabled):not(.--is-invalid):not(:focus-within):hover {
    border-color: ${cssTheme.colors.input.hover.border};
  }

  &.--is-disabled {
    background: ${cssTheme.colors.input.disabled.background};
    border-color: ${cssTheme.colors.input.disabled.border};

    .label {
      background: linear-gradient(
        to top,
        ${cssTheme.colors.input.disabled.background} 50%,
        transparent 50%
      );
      color: ${cssTheme.colors.input.disabled.text};
    }
  }

  input,
  select,
  textarea {
    color: ${cssTheme.colors.input.default.text};
    appearance: none;
    -webkit-appearance: none;
    padding: 0;
    ::-webkit-search-cancel-button {
      display: none;
    }

    &::placeholder {
      color: ${cssTheme.colors.input.default.label.empty};
      text-transform: none;
    }
  }
`

export const InputGroupStyles = css`
  position: relative;
  width: 100%;
`

export const InputTextStyles = css`
  ${cssTheme.typography.bodyM};

  display: block;
  border: none;
  color: ${cssTheme.colors.input.default.text};
  outline: none;
  width: 100%;

  &:disabled {
    background: ${cssTheme.colors.input.disabled.background};
    color: ${cssTheme.colors.input.disabled.text};
  }

  &::placeholder {
    color: ${cssTheme.colors.input.default.label.empty};
  }
`

export const InputLabel = styled.span`
  ${cssTheme.typography.bodyM};

  display: inline-flex;
  align-items: center;

  color: ${cssTheme.colors.input.default.label.empty};
  left: 0;
  position: absolute;
  top: 0;
  height: 100%;
  max-width: 100%;
  white-space: nowrap;
  transition:
    top 0.2s ease,
    left 0.2s ease,
    font-size 0.2s ease,
    color 0.2s ease,
    padding 0.2s ease,
    transform 0.2s ease,
    height 0.2s ease;

  .asterisk {
    color: ${cssTheme.colors.content.danger.secondary};
  }
`

/* The label is absolutely positioned and can't grow the field, so a long translation is truncated */
export const InputLabelText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`

export const CaptionStyles = css`
  ${cssTheme.typography.bodyXS};

  padding: 0 ${cssTheme.layout.spacing.s400};
  color: ${cssTheme.colors.input.default.caption};

  &.--is-invalid {
    color: ${cssTheme.colors.input.error.caption};
  }

  &.--is-disabled {
    color: ${cssTheme.colors.input.disabled.caption};
  }
`
